import type { ProductProps } from '../index';

/** Lumina Cinema — página de produto + checkout embutido. Réplica fiel do
 *  `#rendered-store-canvas` de docs/prototype/lpplat/construtor/code.html (linhas ~325–622),
 *  sem o chrome do editor (sidebar/header/moldura/status — isso mora no painel).
 *
 *  Entrega 1 (visual-first): conteúdo VERBATIM do protótipo. `ProductProps` é aceito; só
 *  `checkoutSlot` é consumido — quando vem (storefront injeta o `<CheckoutForm>` real) ele
 *  ocupa o corpo do card de checkout; sem ele, renderiza os campos estáticos do protótipo.
 *  RSC-safe: sem `'use client'`, sem hooks, só `<a>`. */

const IMG = {
  hero: 'https://lh3.googleusercontent.com/aida-public/AB6AXuA5FUgWuVEDuMpmi-SsgM0MeYsrjBy0TwQFN9Uf7CzAjCDxZkctO8i-yG1ODJJ_mYf6bIbtTw_hSrhdHYfO8SaCEY2_rK_Yro0H-llQWmbqkNMoL6Qw__ghU2mzyqgOSulSvcc_4QBmL_QkVbVEim5aZkpuD7oHZdYl-bqnUIbr1tAwlRMjqKemQ3alDwnUGwpRfOLcKwXFiOLdQA0b4TYxf5BbsCGfmjntvHN-BsbZquejMvoVPcp2gA',
  prod1:
    'https://lh3.googleusercontent.com/aida-public/AB6AXuA_NoeIA6-FKaIrNsiqqPQ7IP38bC28jU4bUVYZinn8aoHv76xMojLC7AYHJdq5MEH44ebMsjWyDHIgfL5MSrcoVss_YqyMbv8LHFSw9zRFpdMXoPAOiJ7xWFOcVXbJJY_-3kOOEKt679Bkd7I-faZ0q2IAzI2T7HO2vewse9MjPk110cjEHAqdXTwUK-LEgK2ZrwVxs8OmTfAcw5IDaKyZamu97mPlaa5HW7U4UmSIGy9QjaHjVafXAQ',
  prod2:
    'https://lh3.googleusercontent.com/aida-public/AB6AXuBjPOuyy7Mbs2X8yAzjWkoy7z4XJ6Y1f4o1av0YxS-8tLny5NAnawnGbA8M6IIqtXn16uo8q6Gidf2cnFUJHbj0IhY5kVLUuUoT92Fc6uMG4ZXv1Ahy6tNwflTuL-4W9FJEHnn_RV-yneynVguei3mpR7LVitvKqkTKV9oRYjfkocv0o-zZ9UlAPzAgp8QQW8n4C6Jgrshix-9RircwKUNkVQLul1FhziKdm4xw-qM_cIRKT5ykR71n1w',
  prod3:
    'https://lh3.googleusercontent.com/aida-public/AB6AXuA0t1qdRNtYH9N_7koODcwJf6t44GSGEZ1N56whD38VBBH275kByv8UQ4xEgBIJRD-017kGVyQBiVdBXDH1iwN0BYPeyhYV-U-Jcbm0qrCdIxAoDk6KiOQWrcx6DDM-YjpyaJgFDd_6bIItLBh6EGlakzHpm6voDeGzBB4i95z8RuY9ZJOL49I0yhyJIW9ymy08d74QqM08lSsoJ3vlwKvJ5XOTG6qHkHJxcKZvegZimv5C63cY5Eg8Mw',
} as const;

function Sym({ name, className, fill }: { name: string; className?: string; fill?: boolean }) {
  return (
    <span
      className={`material-symbols-outlined ${className ?? ''}`}
      style={fill ? { fontVariationSettings: "'FILL' 1" } : undefined}
    >
      {name}
    </span>
  );
}

function GridCard({
  img,
  badge,
  badgeCls,
  name,
  sub,
  price,
}: {
  img: string;
  badge: string;
  badgeCls: string;
  name: string;
  sub: string;
  price: string;
}) {
  return (
    <div className="bg-surface-container rounded-xl p-3 shadow-md hover:bg-surface-container-high transition-all group flex flex-col justify-between">
      <div className="aspect-square w-full rounded-lg overflow-hidden relative mb-3 bg-surface-container-lowest">
        <img
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          src={img}
          alt=""
        />
        <span
          className={`absolute top-2 left-2 font-label-sm text-[10px] px-2 py-0.5 rounded-md font-bold ${badgeCls}`}
        >
          {badge}
        </span>
      </div>
      <div className="flex flex-col gap-1">
        <span className="font-headline-sm text-[0.95rem] font-bold text-white">{name}</span>
        <span className="font-body-sm text-body-sm text-on-surface-variant">{sub}</span>
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-surface-container-highest">
          <span className="font-headline-sm text-headline-sm font-bold text-white">{price}</span>
          <button
            type="button"
            className="p-2 rounded-lg bg-surface-container-high hover:bg-primary hover:text-on-primary text-on-surface transition-colors"
          >
            <Sym name="add_shopping_cart" className="text-[18px]" />
          </button>
        </div>
      </div>
    </div>
  );
}

function VerifiedReview({
  initials,
  initialsCls,
  name,
  place,
  quote,
}: {
  initials: string;
  initialsCls: string;
  name: string;
  place: string;
  quote: string;
}) {
  return (
    <div className="p-space-sm bg-surface-container rounded-xl flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center font-bold font-mono ${initialsCls}`}
          >
            {initials}
          </div>
          <div>
            <div className="font-label-md text-label-md text-white font-bold">{name}</div>
            <div className="font-label-sm text-[11px] text-on-surface-variant">{place}</div>
          </div>
        </div>
        <div className="flex text-amber-400">
          {Array.from({ length: 5 }).map((_, i) => (
            <Sym key={i} name="star" className="text-[14px]" fill />
          ))}
        </div>
      </div>
      <p className="font-body-sm text-body-sm text-on-surface">{quote}</p>
    </div>
  );
}

export default function Product({ checkoutSlot }: ProductProps) {
  return (
    <div className="lumina bg-[#0a0e17] text-white font-body-md antialiased flex flex-col">
      {/* [BLOCO 1] Topbar de urgência */}
      <div className="w-full bg-gradient-to-r from-primary-container via-inverse-primary to-primary-container text-white py-2 px-space-md flex items-center justify-center gap-space-xs font-label-md text-label-md font-bold text-center">
        <Sym name="bolt" className="text-[16px] animate-bounce" />
        <span>Edição Limitada • Frete Grátis acima de R$ 199 + 5% OFF no PIX Instantâneo</span>
        <span className="hidden sm:inline-block font-mono bg-white/20 px-2 py-0.5 rounded text-label-sm">
          CUPOM: LUMINA5
        </span>
      </div>

      {/* Header da loja */}
      <div className="w-full px-space-lg py-4 flex items-center justify-between bg-[#0a0e17]/95 backdrop-blur-md">
        <div className="flex items-center gap-space-md">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-primary to-secondary flex items-center justify-center shadow-lg">
              <Sym name="diamond" className="text-[#0a0e17] text-[20px] font-black" />
            </div>
            <div className="flex flex-col">
              <span className="font-headline-sm text-headline-sm font-extrabold tracking-tight text-white leading-none">
                LUMINA
              </span>
              <span className="font-label-sm text-[9px] uppercase tracking-widest text-on-surface-variant">
                Design &amp; Tech Studio
              </span>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-space-md ml-space-md font-label-md text-label-md text-on-surface-variant">
            <a className="text-white font-bold hover:text-primary transition-colors" href="#">
              Coleção 2025
            </a>
            <a className="hover:text-white transition-colors" href="#">
              Wearables
            </a>
            <a className="hover:text-white transition-colors" href="#">
              Digital Drops
            </a>
            <a className="hover:text-white transition-colors" href="#">
              Garantia VIP
            </a>
          </nav>
        </div>
        <div className="flex items-center gap-space-sm">
          <button
            type="button"
            className="p-2 text-on-surface-variant hover:text-white transition-colors"
          >
            <Sym name="search" className="text-[20px]" />
          </button>
          <div className="relative bg-surface-container-high hover:bg-surface-bright px-3 py-1.5 rounded-full flex items-center gap-2 cursor-pointer transition-colors shadow-sm">
            <Sym name="shopping_bag" className="text-[#10b981] text-[18px]" />
            <span className="font-label-sm text-label-sm font-bold text-white">R$ 289,00</span>
            <span className="w-5 h-5 rounded-full bg-primary-container text-white font-mono text-label-sm flex items-center justify-center font-bold">
              1
            </span>
          </div>
        </div>
      </div>

      {/* [BLOCO 2] Hero de 1 produto */}
      <div className="relative w-full px-space-lg py-space-xl overflow-hidden bg-gradient-to-b from-[#0a0e17] via-surface-container-lowest to-[#0a0e17]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-space-lg items-center relative z-10">
          <div className="lg:col-span-7 flex flex-col gap-space-sm">
            <div className="inline-flex items-center gap-2 bg-surface-container px-3 py-1 rounded-full w-fit">
              <div className="flex text-amber-400">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Sym key={i} name="star" className="text-[16px]" fill />
                ))}
              </div>
              <span className="font-label-sm text-label-sm text-on-surface font-bold">
                4.9/5 (1.428 avaliações reais)
              </span>
            </div>
            <h1 className="font-display-xl text-[2.5rem] md:text-display-xl text-white font-extrabold tracking-tight leading-[1.05]">
              Minimalist Tech Dry. <br />
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                Performance Pura.
              </span>
            </h1>
            <p className="font-body-lg text-body-lg text-on-surface-variant max-w-lg">
              Desenvolvida em fibra molecular termorreguladora de secagem em 90 segundos. Sem odor,
              sem amassar e projetada para produtividade de alta performance.
            </p>

            <div className="flex flex-col gap-2 pt-1">
              <div className="flex items-center justify-between max-w-sm">
                <span className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider font-bold">
                  Selecione o Tamanho:
                </span>
                <span className="font-label-sm text-label-sm text-secondary font-mono">
                  Poucas peças em estoque
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="w-10 h-10 rounded-lg bg-surface-container text-on-surface-variant font-label-md text-label-md hover:bg-surface-bright transition-colors"
                >
                  P
                </button>
                <button
                  type="button"
                  className="w-10 h-10 rounded-lg bg-[#6366f1] text-white font-label-md text-label-md font-bold shadow-md"
                >
                  M
                </button>
                <button
                  type="button"
                  className="w-10 h-10 rounded-lg bg-surface-container text-on-surface-variant font-label-md text-label-md hover:bg-surface-bright transition-colors"
                >
                  G
                </button>
                <button
                  type="button"
                  className="w-10 h-10 rounded-lg bg-surface-container text-on-surface-variant font-label-md text-label-md hover:bg-surface-bright transition-colors"
                >
                  GG
                </button>
                <span className="h-6 w-px bg-surface-container-high mx-1" />
                <div className="flex items-center gap-1 text-on-surface-variant font-label-sm text-label-sm">
                  <Sym name="straighten" className="text-[16px]" />
                  <span>Guia de medidas</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-space-md pt-space-xs">
              <div className="flex flex-col">
                <span className="font-label-sm text-label-sm text-on-surface-variant line-through">
                  De R$ 389,00
                </span>
                <div className="flex items-baseline gap-1">
                  <span className="font-metric-stat text-metric-stat text-white font-extrabold">
                    R$ 289,00
                  </span>
                  <span className="font-label-sm text-label-sm text-[#10b981] font-bold">
                    à vista no PIX
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="w-full sm:w-auto px-space-lg py-3.5 rounded-xl bg-[#10b981] hover:bg-[#059669] text-white font-headline-sm text-[1.1rem] font-extrabold flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(16,185,129,0.5)] hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer"
              >
                <Sym name="flash_on" className="text-[22px]" />
                <span>Comprar Agora com PIX 1-Clique</span>
              </button>
            </div>

            <div className="flex items-center gap-space-md pt-2 text-on-surface-variant font-label-sm text-label-sm">
              <span className="flex items-center gap-1">
                <Sym name="local_shipping" className="text-secondary text-[16px]" />
                Envio Express 24h
              </span>
              <span className="flex items-center gap-1">
                <Sym name="security" className="text-primary text-[16px]" />
                Garantia Blindada 30 dias
              </span>
            </div>
          </div>

          <div className="lg:col-span-5 relative flex justify-center">
            <div className="w-full max-w-[360px] aspect-[4/5] rounded-2xl overflow-hidden relative shadow-2xl bg-surface-container">
              <img className="w-full h-full object-cover" src={IMG.hero} alt="" />
              <div className="absolute top-3 left-3 bg-[#0a0e17]/80 backdrop-blur-md px-3 py-1 rounded-full font-label-sm text-label-sm text-white font-bold flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-secondary" />
                <span>Lote #04 • 84% Reservado</span>
              </div>
              <div className="absolute bottom-3 inset-x-3 bg-surface-container-high/90 backdrop-blur-md p-3 rounded-xl shadow-xl flex items-center justify-between gap-2">
                <div className="flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-lg bg-surface-container flex items-center justify-center shrink-0">
                    <Sym name="watch" className="text-primary text-[20px]" />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-label-sm text-label-sm text-white font-bold">
                      Leve junto: Meia Pro Compression
                    </span>
                    <span className="font-label-sm text-label-sm text-[#10b981] font-mono">
                      + R$ 39,00 (-45% OFF)
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className="px-2.5 py-1.5 rounded-lg bg-primary text-on-primary font-label-sm text-label-sm font-bold shrink-0 hover:bg-primary-container"
                >
                  + Adicionar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* [BLOCO 3] Grade complementar */}
      <div className="w-full px-space-lg py-space-lg bg-[#0a0e17]">
        <div className="flex items-center justify-between mb-space-md">
          <div>
            <h3 className="font-headline-md text-headline-md font-bold text-white">
              Mais Escolhidos da Coleção
            </h3>
            <p className="font-body-sm text-body-sm text-on-surface-variant">
              Pronta entrega com código de rastreio instantâneo
            </p>
          </div>
          <a
            className="font-label-md text-label-md text-primary hover:underline flex items-center gap-1"
            href="#"
          >
            Ver todos <Sym name="chevron_right" className="text-[16px]" />
          </a>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-space-md">
          <GridCard
            img={IMG.prod1}
            badge="Mais Vendido"
            badgeCls="bg-[#10b981] text-white"
            name="Smart Hydration Flask 750ml"
            sub="Isolamento a vácuo 24h"
            price="R$ 149,00"
          />
          <GridCard
            img={IMG.prod2}
            badge="RFID Block"
            badgeCls="bg-primary-container text-white"
            name="Carteira Slim Titânio Carbon"
            sub="Proteção contra clonagem"
            price="R$ 119,00"
          />
          <GridCard
            img={IMG.prod3}
            badge="Hi-Fi Audio"
            badgeCls="bg-amber-400 text-black"
            name="Earbuds Lumina ANC Pro"
            sub="Cancelamento Ativo 40dB"
            price="R$ 349,00"
          />
        </div>
      </div>

      {/* [BLOCO 4] Prova social */}
      <div className="w-full px-space-lg py-space-lg bg-surface-container-lowest">
        <div className="flex items-center gap-2 mb-space-md">
          <Sym name="verified_user" className="text-secondary text-[22px]" />
          <h3 className="font-headline-md text-headline-md font-bold text-white">
            Depoimentos Verificados
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-space-md">
          <VerifiedReview
            initials="RT"
            initialsCls="bg-primary/20 text-primary"
            name="Rodrigo Teixeira"
            place="Comprador Verificado • São Paulo, SP"
            quote={
              '"A camisa Tech Dry é surreal. Viajei por 3 dias e ela se manteve impecável sem passar. O checkout por PIX foi aprovado no mesmo segundo."'
            }
          />
          <VerifiedReview
            initials="ML"
            initialsCls="bg-secondary/20 text-secondary"
            name="Mariana L."
            place="Compradora Verificada • Curitiba, PR"
            quote={
              '"Entrega absurdamente rápida. Veio numa embalagem minimalista sem nenhuma marca estranha, produto com acabamento de alfaiataria premium."'
            }
          />
        </div>
      </div>

      {/* [BLOCO 5] Checkout transparente embutido */}
      <div
        className="w-full px-space-lg py-space-xl bg-gradient-to-b from-surface-container-lowest to-[#0a0e17] scroll-mt-20"
        id="checkout"
      >
        {checkoutSlot ?? (
          <div className="max-w-xl mx-auto bg-surface-container p-space-md rounded-2xl shadow-2xl flex flex-col gap-space-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sym name="lock" className="text-secondary text-[20px]" />
                <span className="font-headline-sm text-[1.1rem] font-bold text-white">
                  Finalização Segura em 1-Passo
                </span>
              </div>
              <span className="font-label-sm text-label-sm text-secondary bg-secondary/15 px-2 py-0.5 rounded font-mono">
                SSL 256 Bits
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
              <div className="flex flex-col gap-1 sm:col-span-2">
                <label className="font-label-sm text-label-sm text-on-surface-variant">
                  Seu Nome Completo
                </label>
                <input
                  className="bg-surface-container-lowest text-white font-label-md text-label-md px-3 py-2 rounded-lg outline-none focus:ring-1 focus:ring-primary"
                  type="text"
                  defaultValue="Gabriel Carvalho"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="font-label-sm text-label-sm text-on-surface-variant">
                  E-mail para Recebimento
                </label>
                <input
                  className="bg-surface-container-lowest text-white font-label-md text-label-md px-3 py-2 rounded-lg outline-none focus:ring-1 focus:ring-primary"
                  type="email"
                  defaultValue="gabriel@exemplo.com.br"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="font-label-sm text-label-sm text-on-surface-variant">
                  WhatsApp / Telefone
                </label>
                <input
                  className="bg-surface-container-lowest text-white font-label-md text-label-md px-3 py-2 rounded-lg outline-none focus:ring-1 focus:ring-primary"
                  type="text"
                  defaultValue="(11) 98765-4321"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1 mt-2">
              <label className="font-label-sm text-label-sm text-on-surface-variant">
                Forma de Pagamento Preferida
              </label>
              <div className="grid grid-cols-3 gap-2">
                <div className="p-2.5 rounded-lg bg-surface-container-high text-white font-label-sm text-label-sm font-bold flex flex-col items-center gap-1 shadow-sm cursor-pointer">
                  <Sym name="qr_code_2" className="text-secondary text-[20px]" />
                  <span>PIX (-5%)</span>
                </div>
                <div className="p-2.5 rounded-lg bg-surface-container-lowest text-on-surface-variant font-label-sm text-label-sm flex flex-col items-center gap-1 cursor-pointer hover:bg-surface-container-high">
                  <Sym name="credit_card" className="text-[20px]" />
                  <span>Cartão em 12x</span>
                </div>
                <div className="p-2.5 rounded-lg bg-surface-container-lowest text-on-surface-variant font-label-sm text-label-sm flex flex-col items-center gap-1 cursor-pointer hover:bg-surface-container-high">
                  <Sym name="receipt_long" className="text-[20px]" />
                  <span>Boleto Bancário</span>
                </div>
              </div>
            </div>
            <button
              type="button"
              className="w-full mt-3 py-3.5 rounded-xl bg-[#10b981] hover:bg-[#059669] text-white font-headline-sm text-headline-sm font-extrabold flex items-center justify-center gap-2 shadow-[0_0_25px_rgba(16,185,129,0.4)] transition-all"
            >
              <Sym name="done_all" className="text-[20px]" />
              <span>Gerar PIX e Concluir Pedido • R$ 289,00</span>
            </button>
          </div>
        )}
      </div>

      {/* Footer white-label */}
      <footer className="w-full px-space-lg py-space-lg bg-[#070a10] flex flex-col gap-space-md border-t border-surface-container-high/30">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-space-md text-on-surface-variant">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <span className="font-headline-sm text-[1.1rem] font-black text-white tracking-tight">
                LUMINA STUDIO
              </span>
              <span className="px-1.5 py-0.5 rounded bg-surface-container-high text-[10px] font-mono text-secondary font-bold">
                PRO
              </span>
            </div>
            <p className="font-body-sm text-[11px] text-on-surface-variant leading-relaxed">
              Tecnologia wearable e vestuário termorregulador minimalista de alta performance.
            </p>
            <p className="font-body-sm text-[10px] text-on-surface-variant/70">
              Infraestrutura e Tecnologia Storeo • CNPJ 48.910.231/0001-90
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="font-label-sm text-label-sm text-white font-bold uppercase tracking-wider">
              Atendimento &amp; Suporte
            </span>
            <a
              className="font-body-sm text-[11px] hover:text-white transition-colors flex items-center gap-1"
              href="#"
            >
              <Sym name="local_shipping" className="text-[14px] text-primary" />
              Rastreio de Pedido Instantâneo
            </a>
            <a
              className="font-body-sm text-[11px] hover:text-white transition-colors flex items-center gap-1"
              href="#"
            >
              <Sym name="support_agent" className="text-[14px] text-secondary" />
              Central de Ajuda &amp; WhatsApp VIP
            </a>
            <span className="font-body-sm text-[10px] text-on-surface-variant/70">
              Segunda a Sexta: 09h às 19h
            </span>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="font-label-sm text-label-sm text-white font-bold uppercase tracking-wider">
              Transparência Legal
            </span>
            <div className="flex flex-col gap-1 text-[11px]">
              <a className="hover:text-white transition-colors" href="#">
                Termos de Uso &amp; Condições Gerais
              </a>
              <a className="hover:text-white transition-colors" href="#">
                Política de Privacidade &amp; LGPD
              </a>
              <a className="hover:text-white transition-colors" href="#">
                Troca e Devolução Grátis em 7 Dias
              </a>
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="font-label-sm text-label-sm text-white font-bold uppercase tracking-wider">
              Segurança do Checkout
            </span>
            <div className="flex flex-wrap gap-1.5">
              <span className="px-2 py-1 rounded bg-surface-container-low text-[10px] text-secondary flex items-center gap-1 font-mono">
                <Sym name="lock" className="text-[12px]" />
                SSL 256-bit
              </span>
              <span className="px-2 py-1 rounded bg-surface-container-low text-[10px] text-primary flex items-center gap-1 font-mono">
                <Sym name="verified" className="text-[12px]" />
                PCI-DSS Level 1
              </span>
              <span className="px-2 py-1 rounded bg-surface-container-low text-[10px] text-on-surface-variant flex items-center gap-1 font-mono">
                <Sym name="policy" className="text-[12px]" />
                LGPD Compliance
              </span>
              <span className="px-2 py-1 rounded bg-surface-container-low text-[10px] text-amber-300 flex items-center gap-1 font-mono">
                <Sym name="shield" className="text-[12px]" />
                Compra Protegida
              </span>
            </div>
          </div>
        </div>
        <div className="pt-space-xs border-t border-surface-container-high/30 flex flex-col md:flex-row items-center justify-between gap-space-sm">
          <div className="flex items-center gap-1.5 flex-wrap justify-center md:justify-start">
            <span className="font-label-sm text-[11px] text-on-surface-variant mr-1">
              Pagamento Seguro:
            </span>
            {['PIX Instantâneo', 'Visa', 'Mastercard', 'Elo', 'Boleto Bancário'].map((b) => (
              <span
                key={b}
                className="px-2 py-0.5 bg-surface-container rounded text-white font-mono text-[10px] font-bold border border-surface-container-highest"
              >
                {b}
              </span>
            ))}
          </div>
          <div className="font-label-sm text-[10px] text-on-surface-variant/80 flex items-center gap-1.5">
            <span>lumina-studio.storeo.com.br • Todos os direitos reservados © 2025</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
